import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DataStats {
  transactions: number;
  accounts: number;
  goals: number;
  categories: number;
  budgets: number;
  recurrences: number;
  reminders: number;
  alerts: number;
  // Estatísticas de dados compartilhados (apenas visualização)
  sharedTransactions: number;
  sharedAccounts: number;
}

interface SharingInfo {
  hasSharedWithOthers: boolean;
  hasAccessToShared: boolean;
  sharedWithCount: number;
  accessToCount: number;
}

export function useClearData() {
  const [isClearing, setIsClearing] = useState(false);
  const [stats, setStats] = useState<DataStats | null>(null);
  const [sharingInfo, setSharingInfo] = useState<SharingInfo | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const { toast } = useToast();

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Single RPC replaces 12 separate count queries
      const { data, error } = await supabase.rpc("get_data_stats");

      if (error) throw error;

      const result = data as any;

      setStats({
        transactions: Number(result?.transactions || 0),
        accounts: Number(result?.accounts || 0),
        goals: Number(result?.goals || 0),
        categories: Number(result?.categories || 0),
        budgets: Number(result?.budgets || 0),
        recurrences: Number(result?.recurrences || 0),
        reminders: Number(result?.reminders || 0),
        alerts: Number(result?.alerts || 0),
        sharedTransactions: Number(result?.sharedTransactions || 0),
        sharedAccounts: Number(result?.sharedAccounts || 0),
      });

      setSharingInfo({
        hasSharedWithOthers: (Number(result?.sharedWithCount || 0)) > 0,
        hasAccessToShared: (Number(result?.accessToCount || 0)) > 0,
        sharedWithCount: Number(result?.sharedWithCount || 0),
        accessToCount: Number(result?.accessToCount || 0),
      });
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const clearTransactions = async () => {
    setIsClearing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Delete apenas dados próprios (user_id = current user)
      // RLS permite deletar dados compartilhados com permissão 'full', 
      // mas queremos proteger esses dados aqui
      await supabase.from("recurrences").delete().eq("user_id", user.id);
      await supabase.from("reminders").delete().eq("user_id", user.id);
      await supabase.from("transactions").delete().eq("user_id", user.id);
      await supabase.from("import_history").delete().eq("user_id", user.id);

      toast({
        title: "Transações removidas",
        description: "Todas as suas transações foram apagadas com sucesso.",
      });
      await fetchStats();
    } catch (error) {
      console.error("Erro ao limpar transações:", error);
      toast({
        title: "Erro",
        description: "Não foi possível limpar as transações.",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const clearTransactionsAndAccounts = async () => {
    setIsClearing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Delete apenas dados próprios
      await supabase.from("recurrences").delete().eq("user_id", user.id);
      await supabase.from("reminders").delete().eq("user_id", user.id);
      await supabase.from("transactions").delete().eq("user_id", user.id);
      await supabase.from("import_history").delete().eq("user_id", user.id);
      await supabase.from("accounts").delete().eq("user_id", user.id);

      toast({
        title: "Dados removidos",
        description: "Suas transações e contas foram apagadas com sucesso.",
      });
      await fetchStats();
    } catch (error) {
      console.error("Erro ao limpar dados:", error);
      toast({
        title: "Erro",
        description: "Não foi possível limpar os dados.",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const clearAllData = async () => {
    setIsClearing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Verificar se tem compartilhamentos ativos
      const { count: activeShares } = await supabase
        .from("account_shares")
        .select("*", { count: "exact", head: true })
        .eq("owner_id", user.id)
        .eq("status", "accepted");

      if (activeShares && activeShares > 0) {
        toast({
          title: "Atenção",
          description: `Você tem ${activeShares} compartilhamento(s) ativo(s). Ao limpar seus dados, os usuários com quem você compartilhou perderão o acesso.`,
          variant: "destructive",
        });
      }

      // Delete apenas dados próprios, em ordem de dependências
      // Primeiro, deletar orçamentos (budget_month_values -> budget_items -> annual_budgets)
      const { data: budgets } = await supabase
        .from("annual_budgets")
        .select("id")
        .eq("user_id", user.id);

      if (budgets && budgets.length > 0) {
        const budgetIds = budgets.map(b => b.id);
        
        // Buscar budget_items desses orçamentos
        const { data: items } = await supabase
          .from("budget_items")
          .select("id")
          .in("budget_id", budgetIds);

        if (items && items.length > 0) {
          const itemIds = items.map(i => i.id);
          await supabase.from("budget_month_values").delete().in("budget_item_id", itemIds);
        }
        
        await supabase.from("budget_items").delete().in("budget_id", budgetIds);
        await supabase.from("annual_budgets").delete().eq("user_id", user.id);
      }

      // Deletar outros dados
      await supabase.from("recurrences").delete().eq("user_id", user.id);
      await supabase.from("reminders").delete().eq("user_id", user.id);
      await supabase.from("transactions").delete().eq("user_id", user.id);
      await supabase.from("import_history").delete().eq("user_id", user.id);
      await supabase.from("accounts").delete().eq("user_id", user.id);
      await supabase.from("goals").delete().eq("user_id", user.id);
      await supabase.from("monthly_plans").delete().eq("user_id", user.id);
      await supabase.from("trend_alerts").delete().eq("user_id", user.id);
      await supabase.from("category_mappings").delete().eq("user_id", user.id);
      await supabase.from("categories").delete().eq("is_system", false).eq("user_id", user.id);
      
      // Deletar compartilhamentos onde sou owner
      await supabase.from("share_invitations").delete().eq("inviter_id", user.id);
      await supabase.from("account_shares").delete().eq("owner_id", user.id);

      toast({
        title: "Conta limpa",
        description: "Todos os seus dados foram apagados. Você pode recomeçar do zero!",
      });
      await fetchStats();
    } catch (error) {
      console.error("Erro ao limpar todos os dados:", error);
      toast({
        title: "Erro",
        description: "Não foi possível limpar todos os dados.",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return {
    isClearing,
    stats,
    sharingInfo,
    isLoadingStats,
    fetchStats,
    clearTransactions,
    clearTransactionsAndAccounts,
    clearAllData,
  };
}
